import { resolveApiBaseURL } from "../config/apiHost";

/**
 * SSO prepaid-membership signup mode. Visiting `/account/signup?sso=true`
 * marks the session for SSO hand-off. After the user authenticates we call the
 * backend, which provisions a paid-but-empty PaymentAssignment and lands them
 * on /onboarding.
 *
 * SSO-prepaid tenants (e.g. TPMG Google login) are provisioned entirely on the
 * backend during social login — they do not use this frontend flag.
 */
export const SSO_PARAM = "sso";
const SSO_STORAGE_KEY = "smsp_sso";

function isTruthy(value: string | null): boolean {
    if (!value) return false;
    return ["1", "true", "yes", "on"].includes(value.toLowerCase());
}

/** If `?sso=true` is present, remember it for the rest of the auth flow. */
export function captureSsoFromSearch(search: string): void {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(search);
    if (isTruthy(params.get(SSO_PARAM))) {
        try {
            window.sessionStorage.setItem(SSO_STORAGE_KEY, "1");
        } catch {
            // ignore unavailable storage
        }
    }
}

/** Whether an SSO hand-off is still pending for this session. */
export function isSsoPending(): boolean {
    if (typeof window === "undefined") return false;
    try {
        return window.sessionStorage.getItem(SSO_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

export function clearSso(): void {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.removeItem(SSO_STORAGE_KEY);
    } catch {
        // ignore
    }
}

/**
 * Absolute URL to the backend sso-session endpoint. The endpoint provisions
 * the empty assignment, then 302s the browser to `redirect` on the tenant.
 */
export function buildSsoSessionUrl(orgSlug?: string | null): string {
    const base = resolveApiBaseURL().replace(/\/$/, "");
    const params = new URLSearchParams();
    if (orgSlug) params.set("org", orgSlug);
    params.set("redirect", "/onboarding"); // hub auto-redirects when single assignment exists
    return `${base}/auth/sso-session/?${params.toString()}`;
}
